import { useForm } from '@tanstack/react-form';
import { Button, Input } from '@ui/themed';
import { t } from 'i18next';
import z from 'zod';
import { useHandlers } from '../../../../context/page-context';
import { CUSTOMER_GROUP_BASE_SLUG } from '../../../customer-groups/constants';
import { PlatformCustomerError } from '../../../customers/errors';
import { useCreatePlatformCustomer } from '../../../customers/hooks/use-create-platform-customer';

// The platform requires a name; this step only collects an email.
const PLACEHOLDER_FIRST_NAME = 'SparkLayer';
const PLACEHOLDER_LAST_NAME = 'Test Customer';

// Single trimmed email field; the refine message doubles as the inline field error.
const emailFormSchema = z.object({
  email: z.email({
    message: t('features:onboarding.components.customer_item.error_invalid_email'),
  }),
});

// Find-or-create a B2B customer from an email (an existing one is adopted as success) and report it via `onSuccess`.
export function CustomerForm({
  onSuccess,
  submitLabel,
}: {
  onSuccess?: (email: string) => void;
  submitLabel: string;
}) {
  const { notificationHandler } = useHandlers();

  const { mutate, isPending } = useCreatePlatformCustomer({
    onSuccess: (customer) => onSuccess?.(customer.email),
    onError: (err, variables) => {
      // An already-existing customer is a no-op success: report it like a normal create so the caller
      // can adopt it. The customers API is B2B-only, so a colliding account is necessarily B2B.
      if (err.id === PlatformCustomerError.Id.AlreadyExists) {
        onSuccess?.(variables.email);
        return;
      }
      // A create failure is operation-level, so surface it as a toast rather than on the input.
      notificationHandler.notify(
        t('notifications:titles.error'),
        t('features:onboarding.components.customer_item.error_generic'),
        'error',
      );
    },
  });

  const form = useForm({
    defaultValues: { email: '' },
    validators: { onSubmit: emailFormSchema },
    onSubmit: ({ value }) => {
      const { email } = emailFormSchema.parse(value);
      mutate({
        email,
        first_name: PLACEHOLDER_FIRST_NAME,
        last_name: PLACEHOLDER_LAST_NAME,
        group: CUSTOMER_GROUP_BASE_SLUG,
        send_invite: true,
      });
    },
  });

  return (
    <form
      className="flex items-start gap-2"
      onSubmit={(event) => {
        event.preventDefault();
        event.stopPropagation();
        void form.handleSubmit();
      }}
    >
      <div className="flex flex-row flex-wrap items-center gap-x-2 gap-y-2">
        <form.Field name="email">
          {(field) => (
            <Input
              name="email"
              placeholder={t('features:onboarding.components.customer_item.email_placeholder')}
              autoComplete="email"
              value={field.state.value}
              onChange={field.handleChange}
              error={field.state.meta.errors[0]?.message}
            />
          )}
        </form.Field>
        <Button type="submit" loading={isPending}>
          {submitLabel}
        </Button>
      </div>
    </form>
  );
}
